import React from 'react';
import { StreamingOverviewResponse } from 'fym-shared/src/music24/streaming';
import { TypedMemo } from '../../../common/types';
import { isSectionAvailable, sectionGivenBy } from '../streamingSources';
import { StreamingTotalsRows } from '../parts/StreamingTotalsRows';
import { StreamingChartBlock } from '../parts/StreamingChartBlock';
import { TopTracksBlock } from '../blocks/TopTracksBlock';
import { DimensionBlock } from '../blocks/DimensionBlock';
import { PlaylistsBlock } from '../blocks/PlaylistsBlock';
import { EarningsBlock } from '../blocks/EarningsBlock';
import { SourcesBlock } from '../blocks/SourcesBlock';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole screen; each part uses its own slice of it */
import styles from '../Streaming.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface OverviewSectionProps {
  accountId: number;
  overview: StreamingOverviewResponse;
  off: string[];
  onNavigate: (path: string) => void;
  onOpenCatalog: () => void;
}

/**
 * Streaming → Overview (design b1): the totals, the second row, the chart, Top tracks, Stores and
 * Countries, Playlists and Earnings, then the sources. Stores and Countries show when a connected
 * source gives them; the Earnings block always shows, with a neutral line when no source reports
 * earnings (owner decision 2026-10-06). Each block loads and fails by itself.
 */
const OverviewSectionComponent: React.FC<OverviewSectionProps> = ({
  accountId,
  overview,
  off,
  onNavigate,
  onOpenCatalog,
}) => (
  <>
    <StreamingTotalsRows overview={overview} off={off} />
    {/* design: onboarding b4. The tour outlines the chart at the numbers stop. */}
    <div data-tour="numbers">
      <StreamingChartBlock accountId={accountId} overview={overview} off={off} />
    </div>
    <div className={styles.blocks}>
      <TopTracksBlock accountId={accountId} days={overview.days} onNavigate={onNavigate} />
      {isSectionAvailable(overview, 'stores') ? (
        <DimensionBlock accountId={accountId} dimension="store" days={overview.days} onNavigate={onNavigate} />
      ) : null}
      {isSectionAvailable(overview, 'countries') ? (
        <DimensionBlock accountId={accountId} dimension="country" days={overview.days} onNavigate={onNavigate} />
      ) : null}
    </div>
    <div className={styles.blocksTwo}>
      <PlaylistsBlock accountId={accountId} days={overview.days} onNavigate={onNavigate} />
      <EarningsBlock
        earnings={overview.earnings}
        givenBy={sectionGivenBy(overview, 'earnings')}
        days={overview.days}
        onNavigate={onNavigate}
        onOpenCatalog={onOpenCatalog}
      />
    </div>
    <SourcesBlock sources={overview.sources} onOpenCatalog={onOpenCatalog} />
  </>
);

export const OverviewSection = TypedMemo(OverviewSectionComponent);
